import templateHTML from "./signup.component.html?raw"
import styleCSS from "./signup.component.scss?raw"

import { OTPDiag } from "../../views/otp.view"
import { type SessionCreateResponse } from "../../models/session-create-response"
import type { AuthService } from "../../services/auth.service"
import type { SignupService } from "../../services/signup.service"
import type { AxiosError } from "axios"

const DIALOG_AUTO_CLOSE_TIMEOUT = 2000

export class SignupComponent extends HTMLElement {
  private otpDiagView!: OTPDiag

  constructor(
    private authService: AuthService = window.tbwServices.authService,
    private signupService: SignupService = window.tbwServices.signupService,
  ) { super() }

  connectedCallback() {
    this.attachShadow({ mode: 'open' })
    const container = document.createElement('div')
    container.innerHTML = templateHTML
    const template = container.querySelector('template')
    const style = document.createElement('style')
    style.textContent = styleCSS
    this.shadowRoot!.appendChild(style)
    this.shadowRoot!.appendChild(template!.content.cloneNode(true))
    this.init()
  }

  private showOTP(email: string) {
    const emailForm = this.shadowRoot!.querySelector("#sign-in-container") as HTMLElement
    const otpForm = this.shadowRoot!.querySelector("#otp-send-container") as HTMLElement
    if (emailForm) emailForm.style.display = "none"
    if (otpForm) otpForm.style.display = "flex"
    this.otpDiagView.open(email)
  }

  private async init() {
    const onOTPConfirmed = async (_session: SessionCreateResponse) => {
      setTimeout(() => window.location.replace('/'), DIALOG_AUTO_CLOSE_TIMEOUT)
    }

    this.otpDiagView = new OTPDiag(
      this.shadowRoot as unknown as HTMLElement,
      onOTPConfirmed,
      this.authService,
    )

    const emailInput = this.shadowRoot!.querySelector<HTMLInputElement>('input[type=email]')!
    const sendBtn = this.shadowRoot!.querySelector<HTMLButtonElement>('button.send-email')!
    const errorContainer = this.shadowRoot!.querySelector<HTMLElement>('.error-container')!
    const errorMessage = this.shadowRoot!.querySelector<HTMLElement>('#error-message')!

    errorContainer.style.display = 'none'

    emailInput.addEventListener('input', () => {
      sendBtn.disabled = !emailInput.checkValidity()
      errorContainer.style.display = 'none'
    })

    sendBtn.addEventListener('click', async () => {
      const email = emailInput.value.trim()
      sendBtn.disabled = emailInput.disabled = true

      try {
        await this.signupService.signup(email)
        this.showOTP(email)
      } catch (err) {
        this.handleError(err as AxiosError, errorContainer, errorMessage)
      } finally {
        sendBtn.disabled = emailInput.disabled = false
      }
    })
  }

  private handleError(error: AxiosError, container: HTMLElement, messageElem: HTMLElement) {
    const data = error.response?.data as { code?: string } | undefined
    const code = data?.code
    const status = error.response?.status

    if (code === 'RATE_LIMITED' || status === 429) {
      messageElem.innerHTML = 'Too many attempts. Please try again in 15 minutes.'
    } else if (code === 'GENERIC_DOMAIN' || (status === 422 && !code)) {
      messageElem.innerHTML = 'Please use your professional email address.'
    } else if (code === 'DOMAIN_NOT_FOUND' || status === 404) {
      messageElem.innerHTML = 'This domain is not recognized. Contact your account manager or reach us at <a href="mailto:contact@thebigwhale.io">contact@thebigwhale.io</a>.'
    } else if (code === 'EMAIL_EXISTS' || status === 409) {
      messageElem.innerHTML = 'An account already exists with this email. <a href="/login">Sign in</a>'
    } else if (code === 'OTP_SEND_FAILED') {
      messageElem.innerHTML = 'Your account was created but the email could not be sent. Please try signing in from the <a href="/login">login page</a> in a few minutes.'
    } else {
      messageElem.innerHTML = 'An error occurred. Please try again.'
    }

    container.style.display = 'flex'
  }
}
